import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Feature, FeatureCollection, MultiPolygon, Polygon } from "geojson";
import type { BuildingProperties } from "../map/layers";

interface Hit {
  feature: Feature<Polygon | MultiPolygon, BuildingProperties>;
  display: string;
  alt?: string;
  centroid: [number, number];
}

interface Props {
  buildings: FeatureCollection<Polygon | MultiPolygon, BuildingProperties> | null;
  onSelect: (centroid: [number, number], building: BuildingProperties) => void;
}

/**
 * Placeholder tiers, longest first. Each step down drops one piece of
 * information so the hint always says something whole — a placeholder cut
 * mid-word ("…try "วิศวกร") is worse than a placeholder that says less.
 *
 * Measured in Chrome at the placeholder's own type style (JetBrains Mono
 * 10.14px, 0.04em tracking): full ≈ 344px, brief ≈ 227px, bare ≈ 120px.
 */
export function hintTiers(count: number): string[] {
  return [
    `Search ${count} buildings — try "engineering" or "วิศวกรรม"`,
    `Search ${count} buildings — try "วิศวกรรม"`,
    `Search ${count} buildings`,
    `Search`,
  ];
}

/** Pixels a hint must leave spare so its last glyph never touches the edge. */
export const HINT_SLACK = 2;

/**
 * The longest tier whose measured width fits `room` px of text box.
 *
 * Pure on purpose: the measured widths come from the caller, so the choice
 * can be reasoned about — and tested — without a browser. When no
 * measurement is available (no layout engine) the fullest hint is kept.
 */
export function pickHint(tiers: string[], widths: number[], room: number): string {
  if (tiers.length === 0) return "";
  if (widths.length !== tiers.length || widths.every((w) => w === 0)) return tiers[0];
  for (let i = 0; i < tiers.length; i++) {
    if (widths[i] <= room - HINT_SLACK) return tiers[i];
  }
  return tiers[tiers.length - 1];
}

function centroid(geom: Polygon | MultiPolygon): [number, number] {
  const ring =
    geom.type === "Polygon"
      ? geom.coordinates[0]
      : geom.coordinates[0][0]; // first ring of first polygon for MultiPolygon
  let sx = 0;
  let sy = 0;
  for (const [x, y] of ring) {
    sx += x;
    sy += y;
  }
  return [sx / ring.length, sy / ring.length];
}

export function BuildingSearch({ buildings, onSelect }: Props) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [hint, setHint] = useState(() => hintTiers(0)[0]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleBlur = () => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    // 350 ms (was 150) gives the user time to move the cursor from the input
    // onto a dropdown row without the list vanishing mid-click.
    timeoutRef.current = window.setTimeout(() => setOpen(false), 350);
  };

  const index: Hit[] = useMemo(() => {
    if (!buildings) return [];
    const out: Hit[] = [];
    for (const f of buildings.features) {
      const p = f.properties;
      const en = p.nameEn ?? null;
      const th = p.nameTh ?? null;
      const generic = p.name ?? null;
      const display = en ?? generic ?? th;
      if (!display) continue;
      const alt = display === th ? en ?? undefined : th ?? undefined;
      out.push({
        feature: f,
        display,
        alt,
        centroid: centroid(f.geometry),
      });
    }
    return out;
  }, [buildings]);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [] as Hit[];
    return index
      .filter((h) => {
        return (
          h.display.toLowerCase().includes(needle) ||
          (h.alt && h.alt.toLowerCase().includes(needle))
        );
      })
      .slice(0, 8);
  }, [index, q]);

  const pick = (h: Hit) => {
    onSelect(h.centroid, h.feature.properties);
    setQ(h.display);
    setOpen(false);
  };

  const count = index.length;

  /**
   * Pick the longest hint that fits the room this field actually has.
   *
   * The room is not a function of viewport width: the field is 92vw on the
   * phone, but the width of the map column it is anchored to above 600px
   * (which is viewport − 400px between 601 and 1024, and − 560px above
   * that). A hint sized for the desktop is cut in half on a 390px phone and
   * again in the 601–769px band, so the field measures instead of guessing.
   */
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;

    const probe = document.createElement("span");
    probe.setAttribute("aria-hidden", "true");
    probe.style.cssText =
      "position:absolute;visibility:hidden;white-space:nowrap;top:-9999px;left:-9999px;";
    document.body.appendChild(probe);

    const fit = () => {
      const cs = getComputedStyle(el);
      const room =
        el.clientWidth -
        (parseFloat(cs.paddingLeft) || 0) -
        (parseFloat(cs.paddingRight) || 0);
      // The placeholder's tracking is a token (--ph-track) so that the
      // measurement and the rendered hint read the same number.
      const fontSize = parseFloat(cs.fontSize) || 0;
      const trackEm = parseFloat(cs.getPropertyValue("--ph-track")) || 0;
      const trackPx = trackEm ? trackEm * fontSize : parseFloat(cs.letterSpacing) || 0;
      probe.style.font = [
        cs.fontStyle || "normal",
        cs.fontWeight || "normal",
        cs.fontSize || "normal",
        cs.fontFamily || "monospace",
      ].join(" ");
      probe.style.letterSpacing = `${trackPx.toFixed(3)}px`;
      probe.style.textTransform = cs.textTransform;
      const tiers = hintTiers(count);
      const widths = tiers.map((t) => {
        probe.textContent = t;
        return probe.getBoundingClientRect().width;
      });
      const next = pickHint(tiers, widths, room);
      setHint((prev) => (prev === next ? prev : next));
    };

    fit();

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(fit);
      ro.observe(el);
    }
    // A webfont arriving after the fit changes the metrics it was measured with.
    if (typeof document !== "undefined" && document.fonts && "ready" in document.fonts) {
      document.fonts.ready.then(fit).catch(() => {});
    }

    return () => {
      if (ro) ro.disconnect();
      probe.remove();
    };
  }, [count]);

  const inputId = "building-search-input";
  const placeholder = buildings ? hint : "Loading buildings…";
  // The fullest hint is always available, even where it cannot be shown.
  const title = buildings ? hintTiers(count)[0] : "Loading buildings…";

  return (
    <div className="building-search">
      <label htmlFor={inputId} className="sr-only">Search campus buildings</label>
      <input
        id={inputId}
        ref={inputRef}
        className="building-search-input mono"
        type="search"
        aria-label="Search campus buildings"
        placeholder={placeholder}
        title={title}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        spellCheck={false}
        autoComplete="off"
      />
      {open && results.length > 0 && (
        <ul className="building-search-results" role="listbox">
          {results.map((h) => (
            <li
              key={h.feature.properties.id ?? `${h.centroid[0]}-${h.centroid[1]}-${h.display}`}
              role="option"
              aria-selected={false}
              tabIndex={0}
              onClick={() => pick(h)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  pick(h);
                }
              }}
              className="building-search-row"
            >
              <span className="building-search-name">{h.display}</span>
              {h.alt && <span className="building-search-alt mono">{h.alt}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
